/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { BottomAction } from '@euler/app/components/BottomAction';
import { Typography } from '@euler/app/components/typography';
import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import { ScrollViewContainer } from '@euler/app/flows/task/pages/inspection/site-inspection/components/ScrollViewContainer';
import { Icon } from '@euler/components/Icomoon';
import {
  ItemProps,
  SectionProps,
  TableView,
} from '@euler/components/TableView';
import { Input } from '@euler/components/form/Input';
import { AppHoldItem, AppMenu } from '@euler/components/holdmenu/AppHoldMenu';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import {
  getDefectiveLevel,
  getDefectiveLevelBgColor,
  getDefectiveLevelColor,
  getDefectiveLevelText,
} from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useFormController } from '@euler/lib/form/controller';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { DefectiveLevel } from '@euler/model';
import { AbnormalLevel, SeverityLevel } from '@euler/model/enum';
import { delayed } from '@euler/utils';
import { useBehaviorSubjectUpdater, usePersistFn } from '@euler/utils/hooks';
import { memo, useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';
import { useSiteInspectionNavigatorContext } from './SiteInspectionContext';
import { usePresentSiteInspectionNavigateAway } from './SiteInspectionNavigator';

type CustomDefectiveFrom = {
  label?: string;
  defectiveLevel?: DefectiveLevel;
  maintenanceAdvice: string;
  description?: string;
};

type Props = CustomDefectiveFrom & {
  optionLabel: string;
  abnormalLevel: AbnormalLevel;
  severityLevel: SeverityLevel;
  title: string;
  onDone?: (values: CustomDefectiveFrom) => void;
};

const DefectiveLevels: DefectiveLevel[] = [
  DefectiveLevel.Fine,
  DefectiveLevel.Urgent,
  DefectiveLevel.Warning,
  DefectiveLevel.Defective,
];

export const CustomDefectiveResultScreen = wrapNavigatorScreen(
  (props: Props) => {
    const insets = useSafeAreaInsets();
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    const { isVoiceInputEnabled } = useVoiceFeatures();

    useToggleAutomaticKeyboard(true);

    const {
      inspection: {
        customIssue: { placeholderColor },
      },
    } = theme;

    usePresentSiteInspectionNavigateAway();

    const { form, validateAll } = useFormController<CustomDefectiveFrom>({
      label: {
        defaultValue: props.label,
      },
      defectiveLevel: {
        defaultValue: getDefectiveLevel(
          props.abnormalLevel,
          props.severityLevel,
        ),
      },
      maintenanceAdvice: {
        required: true,
        defaultValue: props.maintenanceAdvice,
      },
      description: {
        defaultValue: props.description,
      },
    });

    const defectiveLevel =
      form.defectiveLevel.value ??
      getDefectiveLevel(props.abnormalLevel, props.severityLevel);

    const { bottomComponent$ } = useSiteInspectionNavigatorContext();
    const setBottomComponent = useBehaviorSubjectUpdater(bottomComponent$);

    const onDonePress = usePersistFn(async () => {
      const valid = await validateAll();
      if (!valid) {
        return;
      }
      props.onDone?.({
        label: form.label.value || undefined,
        defectiveLevel: form.defectiveLevel.value,
        maintenanceAdvice: form.maintenanceAdvice.value!,
        description: form.description.value || undefined,
      });
    });

    const footer = useMemo(
      () => (
        <BottomAction containerStyle={{ paddingBottom: rpx(8) }}>
          <BottomAction.Button text="完成" onPress={onDonePress} />
        </BottomAction>
      ),
      [onDonePress, rpx],
    );

    useEffect(() => {
      delayed(() => {
        setBottomComponent(footer);
      }, 200);
    }, [footer, setBottomComponent]);

    return (
      <>
        <ScrollViewContainer
          contentContainerStyle={{
            paddingBottom: insets.bottom + rpx(56),
          }}
        >
          <Section
            title="当前状态"
            titleStyle={{ marginLeft: rpx(10), marginTop: rpx(10) }}
          >
            <Row>
              <Input
                placeholderTextColor={placeholderColor}
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                value={form.label.value}
                placeholder="例如: 明显异味"
                errorMsg={form.label.error}
                style={styles.formInput}
                onChangeText={form.label.update}
                onBlur={form.label.onBlur}
              />
            </Row>
          </Section>

          <Section
            title="处理建议"
            titleStyle={{ marginLeft: rpx(10), marginTop: rpx(10) }}
            contentStyle={{ borderWidth: 0 }}
          >
            <Row
              cellContainerStyle={{ paddingLeft: 0, paddingRight: 0 }}
              css={`
                padding: 0;
              `}
            >
              <AppHoldItem
                presentOnPress
                component={({ close }) => (
                  <AppMenu>
                    {DefectiveLevels.map(level => (
                      <AppMenu.Item
                        key={level}
                        title={getDefectiveLevelText(level)}
                        textStyle={{ fontSize: rpx(15) }}
                        onPress={() =>
                          close(() => form.defectiveLevel.update(level))
                        }
                      />
                    ))}
                  </AppMenu>
                )}
              >
                <View
                  css={`
                    border-radius: ${rpx(8)}px;
                    background-color: #fff;
                  `}
                >
                  <HStack
                    css={`
                      background-color: #fff;
                      border-radius: ${rpx(8)}px;
                      overflow: hidden;
                      border-width: 1px;
                      padding: ${rpx(15)}px;
                      border-color: ${getDefectiveLevelColor(
                        theme,
                        defectiveLevel,
                      )};
                      background-color: ${getDefectiveLevelBgColor(
                        theme,
                        defectiveLevel,
                      )};
                    `}
                  >
                    <Typography.Label
                      size={rpx(16)}
                      family={'medium'}
                      color={getDefectiveLevelColor(theme, defectiveLevel)}
                    >
                      {getDefectiveLevelText(defectiveLevel)}
                    </Typography.Label>
                    <Icon name="chevron-down" color="#444" size={rpx(24)} />
                  </HStack>
                </View>
              </AppHoldItem>
            </Row>
          </Section>

          <Section
            title="处理方案"
            titleStyle={{ marginLeft: rpx(10), marginTop: rpx(10) }}
          >
            <Row>
              <Input
                withVoiceInput={isVoiceInputEnabled}
                clearButtonMode="while-editing"
                placeholderTextColor={placeholderColor}
                value={form.maintenanceAdvice.value}
                placeholder="例如: 更换空气滤芯"
                style={styles.formInput}
                errorMsg={form.maintenanceAdvice.error}
                onChangeText={form.maintenanceAdvice.update}
                onBlur={form.maintenanceAdvice.onBlur}
              />
            </Row>
          </Section>

          <Section
            title="故障描述"
            titleStyle={{ marginLeft: rpx(10), marginTop: rpx(10) }}
          >
            <Row>
              <Input
                placeholderTextColor={placeholderColor}
                value={form.description.value}
                placeholder="详细故障描述"
                errorMsg={form.description.error}
                style={styles.formInput}
                onChangeText={form.description.update}
                onBlur={form.description.onBlur}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
                withVoiceInput={isVoiceInputEnabled}
                clearButtonMode="while-editing"
                css={`
                  min-height: ${rpx(60)}px;
                `}
              />
            </Row>
          </Section>
        </ScrollViewContainer>
      </>
    );
  },
  ({ route }) => ({
    title: route.params.title,
    cardStyle: {
      backgroundColor: '#f5f5f5',
    },
  }),
);

const Row = memo(({ separatorStyle, style, ...props }: ItemProps) => {
  const styles = useStyles();
  return (
    <TableView.Item
      style={[styles.row, style]}
      separatorStyle={[styles.separator, separatorStyle]}
      {...props}
    />
  );
});
Row.displayName = 'TableItem';

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  const theme = useTheme();
  return useMemo(
    () =>
      StyleSheet.create({
        row: {
          paddingTop: rpx(5),
          paddingBottom: rpx(5),
          paddingLeft: 0,
          paddingRight: 0,
        },
        separator: {
          marginLeft: rpx(15),
          backgroundColor: '#f2f2f2',
        },
        section: {
          marginTop: rpx(15),
          marginLeft: rpx(15),
          marginRight: rpx(15),
        },
        sectionTitleText: {
          color: theme.inspection.customIssue.labelColor,
          fontSize: rpx(16),
          lineHeight: rpx(18),
          marginBottom: rpx(5),
          fontFamily: FontFamily.NotoSans.Regular,
        },
        sectionContent: {
          backgroundColor: '#fff',
          borderRadius: rpx(8),
          borderWidth: 1,
          borderColor: '#e7e7e7',
          padding: 0,
        },
        formInput: {
          borderWidth: 0,
          backgroundColor: 'transparent',
          paddingTop: rpx(8),
          paddingBottom: rpx(8),
          paddingLeft: 0,
          paddingRight: 0,
          fontSize: rpx(16),
        },
      }),
    [rpx, theme.inspection.customIssue.labelColor],
  );
};

const Section = memo(
  ({ titleTextStyle, contentStyle, style, ...props }: SectionProps) => {
    const styles = useStyles();
    return (
      <TableView.Section
        style={[styles.section, style]}
        titleTextStyle={[styles.sectionTitleText, titleTextStyle]}
        contentStyle={[styles.sectionContent, contentStyle]}
        {...props}
      />
    );
  },
);
Section.displayName = 'TableSection';
